import React from 'react';
import './markdown.css';

// 轻量 Markdown 渲染器（零依赖）
// 支持：##/### 标题、**粗体**、无序/有序列表、表格、引用、分割线
// 输入先做 HTML 转义，再解析结构，杜绝注入

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// 行内解析：**粗体** 与 `代码`
const renderInline = (text: string): React.ReactNode => {
  const nodes: React.ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      nodes.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else {
      nodes.push(<code key={key++}>{token.slice(1, -1)}</code>);
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
};

type Block =
  | { type: 'heading'; level: number; text: string }
  | { type: 'hr' }
  | { type: 'quote'; lines: string[] }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'table'; header: string[]; rows: string[][] }
  | { type: 'paragraph'; text: string };

// 块级解析
const parseBlocks = (raw: string): Block[] => {
  const lines = escapeHtml(raw).split('\n');
  const blocks: Block[] = [];
  let i = 0;

  const isTableRow = (line: string) => /^\s*\|.*\|\s*$/.test(line);
  const splitRow = (line: string) =>
    line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      i++;
      continue;
    }

    // 分割线
    if (/^-{3,}$|^\*{3,}$/.test(trimmed)) {
      blocks.push({ type: 'hr' });
      i++;
      continue;
    }

    // 标题
    const heading = trimmed.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
      i++;
      continue;
    }

    // 表格
    if (isTableRow(trimmed) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1].trim())) {
      const header = splitRow(trimmed);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i].trim())) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    // 引用
    if (trimmed.startsWith('&gt;') || trimmed.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && (lines[i].trim().startsWith('&gt;') || lines[i].trim().startsWith('>'))) {
        quoteLines.push(lines[i].trim().replace(/^(&gt;|>)\s?/, ''));
        i++;
      }
      blocks.push({ type: 'quote', lines: quoteLines });
      continue;
    }

    // 无序列表
    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    // 有序列表
    if (/^\d+[.、]\s*/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+[.、]\s*/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+[.、]\s*/, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    // 普通段落（连续非空、非结构行合并）
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,4})\s+/.test(lines[i].trim()) &&
      !/^[-*]\s+/.test(lines[i].trim()) &&
      !/^\d+[.、]\s*/.test(lines[i].trim()) &&
      !isTableRow(lines[i].trim()) &&
      !(lines[i].trim().startsWith('&gt;') || lines[i].trim().startsWith('>')) &&
      !/^-{3,}$|^\*{3,}$/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    if (paraLines.length) {
      blocks.push({ type: 'paragraph', text: paraLines.join(' ') });
    } else {
      i++;
    }
  }

  return blocks;
};

// 【结论】【数据依据】等全角括号标记行，渲染为小节标签，提升长回答可读性
const SECTION_TAG = /^【[^】]{1,12}】$/;

const Markdown: React.FC<{ content: string }> = ({ content }) => {
  const blocks = parseBlocks(content);

  return (
    <div className="md-body">
      {blocks.map((block, bi) => {
        switch (block.type) {
          case 'heading': {
            if (block.level <= 2) {
              const tagMatch = block.text.match(SECTION_TAG);
              return (
                <h4 className="md-h2" key={bi}>
                  {tagMatch ? <span className="md-section-tag">{block.text}</span> : renderInline(block.text)}
                </h4>
              );
            }
            return (
              <h5 className="md-h3" key={bi}>{renderInline(block.text)}</h5>
            );
          }
          case 'hr':
            return <hr className="md-hr" key={bi} />;
          case 'quote':
            return (
              <blockquote className="md-quote" key={bi}>
                {block.lines.map((l, li) => <p key={li}>{renderInline(l)}</p>)}
              </blockquote>
            );
          case 'ul':
            return (
              <ul className="md-list" key={bi}>
                {block.items.map((it, ii) => <li key={ii}>{renderInline(it)}</li>)}
              </ul>
            );
          case 'ol':
            return (
              <ol className="md-list md-list-ol" key={bi}>
                {block.items.map((it, ii) => <li key={ii}>{renderInline(it)}</li>)}
              </ol>
            );
          case 'table':
            return (
              <div className="md-table-wrap" key={bi}>
                <table className="data-table md-table">
                  <thead>
                    <tr>
                      {block.header.map((h, hi) => <th key={hi}>{renderInline(h)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => <td key={ci}>{renderInline(cell)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'paragraph':
          default: {
            const tagMatch = block.text.match(SECTION_TAG);
            if (tagMatch) {
              return (
                <h4 className="md-h2" key={bi}>
                  <span className="md-section-tag">{block.text}</span>
                </h4>
              );
            }
            return <p className="md-p" key={bi}>{renderInline(block.text)}</p>;
          }
        }
      })}
    </div>
  );
};

export default Markdown;
